<template>
  <div style="height: inherit">
    <div
      class="body-content-overlay"
      :class="{ show: mqShallShowLeftSidebar }"
      @click="mqShallShowLeftSidebar = false"
    />

    <section class="chat-app-window">
      <!-- Estados globais do chat -->
      <div
        v-if="!isReady"
        class="start-chat-area"
      >
        <template v-if="isLoading">
          <b-spinner
            variant="primary"
            class="mb-1"
          />
          <h4 class="start-chat-text">
            Carregando conversas…
          </h4>
        </template>
        <template v-else>
          <div class="mb-1 start-chat-icon">
            <feather-icon
              :icon="stateIcon"
              size="56"
            />
          </div>
          <h4 class="start-chat-text text-center">
            {{ stateMessage }}
          </h4>
          <b-button
            v-if="canRetry"
            variant="primary"
            class="mt-1"
            @click="retry"
          >
            Tentar novamente
          </b-button>
        </template>
      </div>

      <!-- Nenhuma conversa aberta -->
      <div
        v-else-if="!openConversation"
        class="start-chat-area"
      >
        <div class="mb-1 start-chat-icon">
          <feather-icon
            icon="MessageSquareIcon"
            size="56"
          />
        </div>
        <h4
          class="sidebar-toggle start-chat-text"
          @click="showSidebarOnSmallScreens"
        >
          Selecione uma conversa
        </h4>
      </div>

      <chat-conversation
        v-else
        :conversation="openConversation"
        @show-sidebar="mqShallShowLeftSidebar = true"
      >
        <template #actions="{ conversation }">
          <conversation-participants :conversation="conversation" />
        </template>
      </chat-conversation>
    </section>

    <portal
      v-if="isReady"
      to="content-renderer-sidebar-left"
    >
      <chat-inbox-sidebar
        :active-conversation-id="openConversationId"
        :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
        @open-conversation="selectConversation"
      />
    </portal>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app'
import { $themeBreakpoints } from '@themeConfig'
import * as types from '../store/types'
import {
  CHAT_STATUS_IDLE,
  CHAT_STATUS_LOADING,
  CHAT_STATUS_READY,
  CHAT_STATUS_UNAVAILABLE,
  CHAT_STATUS_FORBIDDEN,
} from '../store/state'
import ChatInboxSidebar from '../components/ChatInboxSidebar.vue'
import ChatConversation from '../components/ChatConversation.vue'
import ConversationParticipants from '../components/ConversationParticipants.vue'

export default {
  components: {
    BButton,
    BSpinner,
    ChatInboxSidebar,
    ChatConversation,
    ConversationParticipants,
  },
  setup() {
    const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()
    return { toast: useToast(), mqShallShowLeftSidebar }
  },
  computed: {
    ...mapGetters({
      status: types.STATUS,
      openConversation: types.OPEN_CONVERSATION,
    }),
    openConversationId() {
      return this.openConversation ? this.openConversation.conversationId : null
    },
    routeConversationId() {
      const { conversation } = this.$route.query
      return typeof conversation === 'string' && conversation ? conversation : null
    },
    isReady() {
      return this.status === CHAT_STATUS_READY
    },
    isLoading() {
      return this.status === CHAT_STATUS_LOADING || this.status === CHAT_STATUS_IDLE
    },
    canRetry() {
      return this.status !== CHAT_STATUS_FORBIDDEN
    },
    stateIcon() {
      return this.status === CHAT_STATUS_FORBIDDEN ? 'LockIcon' : 'CloudOffIcon'
    },
    stateMessage() {
      if (this.status === CHAT_STATUS_UNAVAILABLE) return 'O chat com clientes está indisponível no momento. Tente novamente mais tarde.'
      if (this.status === CHAT_STATUS_FORBIDDEN) return 'Você não tem permissão para acessar o chat com clientes.'
      return 'Não foi possível carregar o chat com clientes.'
    },
  },
  watch: {
    // A conversa aberta acompanha ?conversation= (deep link vindo da ficha do cliente).
    routeConversationId() {
      this.syncRouteConversation()
    },
    status(value) {
      if (value === CHAT_STATUS_READY) this.syncRouteConversation()
    },
  },
  created() {
    this.$store.dispatch(types.INIT)
    this.syncRouteConversation()
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.$store.dispatch(types.SET_VIEW_VISIBLE, document.visibilityState !== 'hidden')
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.$store.dispatch(types.SET_VIEW_VISIBLE, false)
    this.$store.dispatch(types.CLOSE_CONVERSATION)
  },
  methods: {
    onVisibilityChange() {
      this.$store.dispatch(types.SET_VIEW_VISIBLE, document.visibilityState !== 'hidden')
    },
    retry() {
      this.$store.dispatch(types.INIT)
    },
    showSidebarOnSmallScreens() {
      if (this.$store.state.app.windowWidth < $themeBreakpoints.lg) this.mqShallShowLeftSidebar = true
    },
    selectConversation(conversationId) {
      this.mqShallShowLeftSidebar = false
      if (conversationId === this.routeConversationId) {
        if (conversationId !== this.openConversationId) this.syncRouteConversation()
        return
      }
      this.$router.push({ name: 'customer-chat', query: { conversation: conversationId } }).catch(() => {})
    },
    syncRouteConversation() {
      if (!this.isReady) return
      const conversationId = this.routeConversationId
      if (!conversationId) {
        if (this.openConversationId) this.$store.dispatch(types.CLOSE_CONVERSATION)
        return
      }
      if (conversationId === this.openConversationId) return
      this.$store.dispatch(types.OPEN_CONVERSATION_BY_ID, conversationId).catch(error => {
        this.$store.dispatch(types.CLOSE_CONVERSATION)
        this.notifyOpenError(error)
      })
    },
    notifyOpenError(error) {
      let text = 'Não foi possível abrir a conversa. Tente novamente.'
      if (error && error.status === 403) text = 'Você não participa desta conversa.'
      else if (error && error.status === 404) text = 'Conversa não encontrada.'
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'Chat com clientes',
          text,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/pages/app-chat.scss";
@import "~@core/scss/base/pages/app-chat-list.scss";

// Ajustes do template Vuexy para cabeçalho/composer de altura variável e
// para as bolhas do chat com clientes.
.chat-application .chat-app-window {
  .customer-chat-active {
    display: flex;
    flex-direction: column;
  }
  .customer-chat-header {
    height: auto;
    min-height: 65px;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    align-items: center;
  }
  .customer-chat-scroller {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
    overflow-y: auto;
  }
  .customer-chat-typing {
    min-height: 1.4rem;
    padding: 0 1rem;
    background-color: #fff;
  }
  .customer-chat-composer {
    height: auto;
    min-height: 65px;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .customer-chat-system {
    clear: both;
    text-align: center;
    margin: 0.5rem 0 1rem;
  }
  .customer-chat-system-text {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    background-color: rgba(34, 41, 47, 0.08);
    color: #6e6b7b;
    font-size: 0.85rem;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .chats .chat-body {
    margin-right: 0;
  }
  .chats .chat-left .chat-body {
    margin-left: 0;
  }
  .chats .chat-body .customer-chat-bubble {
    // Texto puro: preserva quebras de linha sem interpretar HTML.
    .customer-chat-text {
      white-space: pre-wrap;
      word-break: break-word;
      margin: 0;
    }
    .customer-chat-sender {
      display: block;
      font-weight: 600;
      opacity: 0.85;
      margin-bottom: 0.15rem;
    }
    .customer-chat-meta {
      display: block;
      text-align: right;
      opacity: 0.75;
      font-size: 0.75rem;
      margin-top: 0.15rem;
    }
    // ✓ enviada herda a cor do texto da bolha; ✓✓ lida ganha cor própria (além do segundo
    // check — nunca só a cor) e opacidade cheia.
    .customer-chat-delivery {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
      margin-left: 0.25rem;
    }
    .customer-chat-delivery--read {
      color: #28c76f;
    }
  }
  .chats .chat-body .customer-chat-bubble--other-staff {
    background: none;
    background-color: #e8f4fb;
    color: #5e5873;
  }
  .chats .chat-body .customer-chat-bubble--failed {
    opacity: 0.7;
  }
  .customer-chat-failed-actions {
    clear: both;
    float: right;
    margin: -6px 1rem 10px 0;
  }
}

.chat-application .customer-chat-sidebar {
  .customer-chat-list-wrapper {
    overflow-y: auto;
  }
  // As abas vivem dentro de .chat-user-list-wrapper, cujo `li` do Vuexy (feito para a lista de
  // contatos) ganha padding de ~1.3rem por lado e fundo no hover. Somado aos dois rótulos, isso
  // estourava os 360px do sidebar e o .nav (flex-wrap) jogava a segunda aba para baixo.
  .customer-chat-tabs {
    flex-wrap: nowrap;
    padding: 0.5rem 0.5rem 0;
    > li.nav-item {
      display: block;
      flex: 1 1 0;
      min-width: 0;
      padding: 0;
      &:hover {
        background: none;
      }
    }
    .nav-link {
      display: flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      padding: 0.5rem 0.25rem;
      font-size: 0.9rem;
    }
    .nav-link .badge {
      flex-shrink: 0;
    }
  }
}

.dark-layout .chat-application .chat-app-window {
  .customer-chat-typing {
    background-color: transparent;
  }
  .chats .chat-body .customer-chat-bubble--other-staff {
    background-color: #2c3f55;
    color: #d0d2d6;
  }
}
</style>
